// /src/services/approvalsService.js
import { db } from "./firebase";
import { collection, getDocs, query, where, limit, orderBy } from "firebase/firestore";
import { normalizeFranchiseName } from "./normalize";


const approvalsCol = collection(db, "approvals");

// 개인/법인 판별: payload.franciseName 끝 4자리가 숫자면 개인
export function detectMemberType(franciseName) {
    const s = String(franciseName || "");
    return /\d{4}$/.test(s) ? "personal" : "corporate";
}

/**
 * approvals 에서 활성(active) + (전화번호 일치 OR 이름 일치) 문서 탐색
 * 우선순위: phoneE164 완전 일치 > name 일치
 * 반환: { id, franchiseName: payload.franciseName, memberType, data }
 */
export async function findActiveApprovalForUser({ phoneE164, name }) {
    const col = collection(db, "approvals");

    // 1) phoneE164 일치 우선
    let q1 = query(col, where("status", "==", "active"), where("phoneE164", "==", phoneE164), limit(50));
    let snap = await getDocs(q1);
    let found = null;

    snap.forEach(doc => {
        const d = doc.data();
        const f = d?.payload?.franciseName || d?.payload?.franchiseName || d?.carrierName || "";
        if (!f) return;
        found = { id: doc.id, franchiseName: f, memberType: detectMemberType(f), data: d };
    });

    if (found) return found;

    // 2) 이름 일치(여럿일 수 있음 → 첫건)
    let q2 = query(col, where("status", "==", "active"), where("name", "==", name), limit(50));
    snap = await getDocs(q2);
    let candidate = null;
    snap.forEach(doc => {
        const d = doc.data();
        const f = d?.payload?.franciseName || d?.payload?.franchiseName || d?.carrierName || "";
        if (!f) return;
        if (!candidate) candidate = { id: doc.id, franchiseName: f, memberType: detectMemberType(f), data: d };
    });
    return candidate; // 없으면 null
}
/* ─────────────────────────────────────────────────────────────
   여기서부터 멤버(name + carNumber 뒤 4자리) 기준 연결용
----------------------------------------------------------------*/


// /src/services/approvalsService.js 일부

function getCarLast4(v) {
    if (!v) return "";
    const s = String(v).replace(/\s/g, "");
    return s.slice(-4);
}

export async function findApprovalForMemberByPhoneE164(phoneE164) {
    if (!phoneE164) return null;

    const q = query(
        approvalsCol,
        where("payload.phoneE164", "==", phoneE164),
        where("status", "==", "active"),
        limit(1)
    );

    const snap = await getDocs(q);
    if (snap.empty) {
        console.log("[approvalsService] no approval for phoneE164:", phoneE164);
        return null;
    }

    const doc = snap.docs[0];
    const raw = doc.data() || {};
    const p = raw.payload || {};

    // 🔹 여기서 carrierName을 우리가 쓰는 franchiseName으로 삼는다
    const franchiseName = p.carrierName || "";

    const approval = {
        id: doc.id,
        phoneE164: p.phoneE164 || "",
        franchiseName,
        carrierName: p.carrierName || "",
        carNumber: p.carNumber || "",
        hqName: p.hqName || "",
        raw,
    };

    console.log("[approvalsService] approval by phoneE164 =", approval);
    return approval;
}

/**
 * 멤버 ↔ approvals 연결 전용
 * - members: name + carNumber 뒤 4자리
 * - approvals: payload.name + payload.carNumber
 */
export async function findApprovalForMemberByNameAndCar({ name, carNumber }) {
    if (!name || !carNumber) return null;

    const targetTail = getCarLast4(carNumber);
    const col = collection(db, "approvals");

    // 🔹 1차: payload.name 으로 필터링 (status=active)
    const q = query(
        col,
        where("status", "==", "active"),
        where("payload.name", "==", name),   // ← 여기! Name → name, payload 경로
        limit(50)
    );

    const snap = await getDocs(q);

    let found = null;

    snap.forEach(docSnap => {
        if (found) return;
        const d = docSnap.data() || {};
        const payload = d.payload || {};

        const rawCar = payload.carNumber || payload.CarNumber || "";
        const tail = getCarLast4(rawCar);
        if (tail !== targetTail) return;

        const f =
            payload.franchiseName ||
            d.franchiseName ||
            payload.carrierName ||
            d.carrierName ||
            "";

        if (!f) return;

        found = {
            id: docSnap.id,
            franchiseName: f,
            memberType: detectMemberType(f),
            data: d,
        };
    });

    return found;
}


// 이미 있으면 중복 선언 안 해도 됨
const COL_SETTLEMENTS = collection(db, "settlements_raw");

// 🔹 settlements_raw에서 "가장 최신 settlementMonthKey" 찾기
async function getLatestSettlementMonthKey(settlementType) {
    const clauses = [];
    if (settlementType) clauses.push(where("settlementType", "==", settlementType));

    // 최신 달 1개만 본다 (admin 쪽이랑 동일 구조면 orderBy + limit 써도 되고,
    // 이미 거기서 잘 돌아가고 있다면 그대로 가져오면 됨)
    const qRef = query(
        COL_SETTLEMENTS,
        ...clauses,
        // 최신 monthKey 기준 정렬
        orderBy("settlementMonthKey", "desc"),
        limit(1)
    );

    const snap = await getDocs(qRef);
    if (snap.empty) return null;

    const d = snap.docs[0].data() || {};
    return d.settlementMonthKey || null;
}

// 🔹 특정 월 기준 매출 Map 생성
//   - opts.settlementMonthKey 없으면: settlements_raw에서 "가장 최신 월" 자동 탐색
//   - opts.settlementType 있으면: 해당 타입만 필터링
//   - 반환: Map<franchiseKey, sumFare>
export async function buildSalesMapFromSettlements(opts = {}) {
    const { settlementMonthKey, settlementType } = opts || {};

    let monthKey = settlementMonthKey;
    if (!monthKey) {
        monthKey = await getLatestSettlementMonthKey(settlementType);
        if (!monthKey) {
            console.warn("[buildSalesMapFromSettlements] no settlements found");
            return new Map();
        }
    }

    const clauses = [where("settlementMonthKey", "==", monthKey)];
    if (settlementType) clauses.push(where("settlementType", "==", settlementType));

    const qRef = query(COL_SETTLEMENTS, ...clauses);
    const snap = await getDocs(qRef);

    const map = new Map();

    snap.forEach((docSnap) => {
        const data = docSnap.data() || {};
        const payload = data.payload || {};

        const rawFran = payload.franchiseName || data.franchiseName || "";
        if (!rawFran) return;

        const key = String(rawFran).trim(); // 🔹 admin쪽 normalizeFran 과 동일하게 trim만
        if (!key) return;

        const fareRaw = payload.totalFare ?? data.totalFare ?? payload.fare ?? 0;
        const fare =
            typeof fareRaw === "number"
                ? fareRaw
                : Number(String(fareRaw).replace(/[,\s]/g, "")) || 0;

        if (!fare) return;

        map.set(key, (map.get(key) || 0) + fare);
    });

    console.log(
        "[buildSalesMapFromSettlements] monthKey=",
        monthKey,
        "type=",
        settlementType,
        "size=",
        map.size
    );
    return map;
}